// Public read-only itinerary at /?share=<token>. Fetches GET /api/tp/share/
// :token (no auth; server strips bookings/userId and decodes customPlan to a
// JSON string). Mirrors the app's day-building rules: manual customPlan via
// buildDaysFromPlan, otherwise packItinerary. List / Outlook / Map views.
import React from 'react';
import { PALETTE } from '../theme';
import { CAT_COLORS } from '../data';
import { packItinerary, packMultiLegItinerary, computeEventLanes, buildDaysFromPlan, fmtTime, parseFlightHM, parseFlightStr } from './itinerary/logic';
import { getIdeasForDest, getIdeasForLegs } from '../data';
import { sortLegsChrono } from '../utils/scanDetails';
import { MapView } from '../components/MapView';

// ── Helpers ───────────────────────────────────────────────────────────────────


function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

// A stable per-device id (not an account) so repeat check-ins from the same
// browser update one guest entry instead of piling up duplicates.
function getOrCreateGuestId() {
  let id = localStorage.getItem('tp_guest_id');
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `g_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    localStorage.setItem('tp_guest_id', id);
  }
  return id;
}

function fmtLastSeen(ts) {
  if (!ts) return '';
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

// One-time (per token, per device) "what's your name" prompt so guests show
// up by name in the presence list — not a real login, just a courtesy so the
// owner (and other guests) can see who's looking. Skippable; view stays
// read-only either way.
function GuestNamePrompt({ onSubmit, onSkip }) {
  const [name, setName] = React.useState('');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,18,14,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}>
      <div style={{ background: PALETTE.paper, borderRadius: 14, padding: 24, maxWidth: 340, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ fontFamily: 'Fraunces', fontSize: 20, color: PALETTE.ink, marginBottom: 6 }}>Who's viewing?</div>
        <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute, lineHeight: 1.5, marginBottom: 16 }}>
          Add your name so others looking at this itinerary can see you're here. This is read-only — you won't be able to edit anything.
        </div>
        <input
          autoFocus
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && name.trim() && onSubmit(name.trim())}
          placeholder="Your name"
          maxLength={40}
          style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '9px 12px', fontFamily: 'Inter', fontSize: 14, color: PALETTE.ink, outline: 'none', marginBottom: 14 }}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={() => name.trim() && onSubmit(name.trim())}
            disabled={!name.trim()}
            style={{ appearance: 'none', border: 0, cursor: name.trim() ? 'pointer' : 'default', background: name.trim() ? PALETTE.ink : PALETTE.rule, color: name.trim() ? PALETTE.paper : PALETTE.inkMute, fontFamily: 'Inter', fontSize: 13, fontWeight: 600, padding: '8px 16px', borderRadius: 999 }}
          >Continue</button>
          <button onClick={onSkip} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, padding: '8px 4px' }}>
            Skip, view anonymously
          </button>
        </div>
      </div>
    </div>
  );
}



// Exported for tests: mirrors the app's day-building rules for shared trips.
export function buildDisplayDays(trip, allIdeas) {
  const { selectedIdeas = [], customPlan, dayCount = 5, dateStart, ideaOverrides = {} } = trip;
  const legs = trip.legs && trip.legs.length > 1 ? trip.legs : null;

  let days;
  if (customPlan) {
    let plan;
    try { plan = typeof customPlan === 'string' ? JSON.parse(customPlan) : customPlan; } catch { plan = null; }
    if (Array.isArray(plan)) {
      days = buildDaysFromPlan(plan, dateStart, dayCount, allIdeas, ideaOverrides, legs);
    }
  }

  if (!days) {
    const packed = legs
      ? packMultiLegItinerary(legs, selectedIdeas, dateStart, allIdeas)
      : packItinerary(selectedIdeas, dayCount, dateStart, allIdeas);
    days = packed.days;
  }

  return days.map(day => ({
    ...day,
    items: day.items.slice().sort((a, b) => {
      const aStart = ideaOverrides[a.idea.id]?.startTime ?? a.start;
      const bStart = ideaOverrides[b.idea.id]?.startTime ?? b.start;
      return aStart - bStart;
    }),
  }));
}



// Mirrors ItineraryScreen's flightByDay: maps day index → array of flight
// bands. Flights aren't forced into "outbound/return" — a multi-city trip
// is just N legs — so each is colored by its position in the trip's
// chronological flight order and labeled by its actual route.
function buildFlightByDay(sharedFlights, dayCount, dateStart) {
  const legs = sharedFlights?.legs || [];
  if (legs.length === 0) return {};
  const last = dayCount - 1;
  const dateToIdx = (dateStr) => {
    if (!dateStr || !dateStart) return 0;
    const tripMs = new Date(dateStart + 'T12:00:00').getTime();
    const flightMs = new Date(dateStr + 'T12:00:00').getTime();
    const offset = Math.round((flightMs - tripMs) / 86400000);
    return Math.max(0, Math.min(last, offset));
  };
  const sortedLegs = sortLegsChrono(legs);
  const result = {};
  sortedLegs.forEach((leg, seq) => {
    const idx = dateToIdx(leg.date);
    const band = {
      id: leg.id, seq, airline: leg.airline,
      flightNo: leg.flightNo, date: leg.date,
      depTime: leg.depTime, arrTime: leg.arrTime,
      origin: leg.origin, dest: leg.dest,
    };
    (result[idx] = result[idx] || []).push(band);
  });
  Object.values(result).forEach(bands => bands.sort((a, b) => (a.depTime || '').localeCompare(b.depTime || '')));
  return result;
}

const SHARED_FLIGHT_PALETTE = [
  { bg: '#EFF6FF', border: '#BFDBFE', label: '#3B82F6', mono: '#1E40AF', dark: '#1E3A8A', line: '#BFDBFE', arrow: '#60A5FA' },
  { bg: '#F5F0FF', border: '#DDD0FA', label: '#8B5CF6', mono: '#6D28D9', dark: '#5B21B6', line: '#DDD0FA', arrow: '#A78BFA' },
  { bg: '#FFF8EB', border: '#FDE1A8', label: '#D97706', mono: '#B45309', dark: '#92400E', line: '#FDE1A8', arrow: '#F59E0B' },
  { bg: '#ECFDF5', border: '#A7F3D0', label: '#10B981', mono: '#047857', dark: '#065F46', line: '#A7F3D0', arrow: '#34D399' },
];

function FlightBand({ band }) {
  const c = SHARED_FLIGHT_PALETTE[(band.seq ?? 0) % SHARED_FLIGHT_PALETTE.length];
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const fmtD = (d) => {
    if (!d) return '';
    const [, mo, day] = (d || '').split('-');
    return mo && day ? `${parseInt(day)} ${MONTHS[parseInt(mo) - 1]}` : d;
  };
  let { depTime, arrTime, origin, dest, flightNo, date, fallback, airline } = band;
  if (!depTime && !arrTime && fallback) {
    const p = parseFlightStr(fallback);
    depTime = p.depTime; arrTime = p.arrTime;
    if (!origin) origin = p.origin;
    if (!dest) dest = p.dest;
    if (!flightNo) flightNo = p.flightNo;
    if (!date) date = p.date;
  }
  const hasTime = depTime || arrTime;
  const route = [origin, dest].filter(Boolean).join(' → ');
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', borderRadius: 8, background: c.bg, border: `0.5px solid ${c.border}` }}>
      <span style={{ fontSize: 13, flexShrink: 0, marginTop: 2 }}>✈️</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: hasTime ? 6 : 2, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: c.label, fontWeight: 600 }}>{route || 'Flight'}</span>
          {flightNo && <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: c.mono }}>{flightNo}</span>}
          {(date) && <span style={{ fontFamily: 'Inter', fontSize: 11, color: c.label }}>{fmtD(date)}</span>}
          {airline && !flightNo && <span style={{ fontFamily: 'Inter', fontSize: 11, color: c.mono, fontWeight: 500 }}>{airline}</span>}
        </div>
        {hasTime ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ textAlign: 'center', minWidth: 36 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 17, color: c.dark, fontWeight: 700, lineHeight: 1 }}>{depTime || '—'}</div>
              {origin && <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: c.label, marginTop: 2 }}>{origin}</div>}
            </div>
            <div style={{ flex: 1, height: 1, background: c.line, position: 'relative', minWidth: 16 }}>
              <div style={{ position: 'absolute', right: -3, top: '50%', transform: 'translateY(-50%)', color: c.arrow, fontSize: 12, lineHeight: 1 }}>›</div>
            </div>
            <div style={{ textAlign: 'center', minWidth: 36 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 17, color: c.dark, fontWeight: 700, lineHeight: 1 }}>{arrTime || '—'}</div>
              {dest && <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: c.label, marginTop: 2 }}>{dest}</div>}
            </div>
          </div>
        ) : fallback ? (
          <div style={{ fontFamily: 'Inter', fontSize: 11, color: c.dark, lineHeight: 1.4 }}>{fallback}</div>
        ) : null}
      </div>
    </div>
  );
}

// ── Calendar constants ────────────────────────────────────────────────────────
const CAL_CAT_COLORS = {
  Food:        { bg: '#FDF0E8', border: '#C06040', text: '#7A3520' },
  Sights:      { bg: '#F4EEE4', border: '#8A7050', text: '#504028' },
  Experience:  { bg: '#EFE8F6', border: '#6050A0', text: '#342450' },
  'Day trip':  { bg: '#E6F4EC', border: '#507868', text: '#2A4840' },
  Nightlife:   { bg: '#E0E4F0', border: '#3A4870', text: '#1A2035' },
  Shopping:    { bg: '#F5EAF4', border: '#907090', text: '#584060' },
  Hotel:       { bg: '#E4EEF6', border: '#5A7898', text: '#304858' },
  Custom:      { bg: '#F6EAEA', border: '#907070', text: '#584040' },
};
const CAL_START_HOUR = 0;
const CAL_END_HOUR = 24;
const CAL_HOUR_PX = 64;
const CAL_HOURS = Array.from({ length: CAL_END_HOUR - CAL_START_HOUR }, (_, i) => CAL_START_HOUR + i);
const MIN_COL_W = 110;

// ── Read-only calendar outlook view ───────────────────────────────────────────
function OutlookView({ days, ideaOverrides, accommodations = {}, accomBooked = {}, flightByDay = {} }) {
  const [dayOffset, setDayOffset] = React.useState(0);
  const scrollRef = React.useRef(null);
  const [colsMode, setColsMode] = React.useState('auto'); // 'auto' | '1' | '3' | 'all' | number string
  const [customInput, setCustomInput] = React.useState('');

  const [autoColsPerView, setAutoColsPerView] = React.useState(2);
  React.useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setAutoColsPerView(w >= 700 ? Math.min(days.length, 4) : w >= 480 ? Math.min(days.length, 3) : 2);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [days.length]);

  const colsPerView = React.useMemo(() => {
    if (colsMode === 'auto') return autoColsPerView;
    if (colsMode === 'all') return days.length;
    const n = parseInt(colsMode, 10);
    if (!isNaN(n) && n > 0) return Math.min(n, days.length);
    return autoColsPerView;
  }, [colsMode, autoColsPerView, days.length]);

  const maxOffset = Math.max(0, days.length - colsPerView);
  const safeOffset = Math.min(dayOffset, maxOffset);
  const visibleDays = days.slice(safeOffset, safeOffset + colsPerView);

  const getStart = (slot) => ideaOverrides[slot.idea.id]?.startTime ?? slot.start;
  const getDur   = (slot) => ideaOverrides[slot.idea.id]?.dur ?? slot.idea.dur;

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = (9 - CAL_START_HOUR) * CAL_HOUR_PX - 20;
  }, []);

  const hasAnyAccom = visibleDays.some((_, vi) => accommodations[safeOffset + vi]?.name);

  const applyCustomInput = () => {
    const n = parseInt(customInput, 10);
    if (!isNaN(n) && n >= 1) { setColsMode(String(n)); setDayOffset(0); }
  };

  const activeColsMode = (() => {
    if (colsMode === '1') return '1';
    if (colsMode === '3') return '3';
    if (colsMode === 'all') return 'all';
    const n = parseInt(colsMode, 10);
    if (!isNaN(n) && colsMode !== 'auto') return 'custom';
    return 'auto';
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Day range mode controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderBottom: `0.5px solid ${PALETTE.ruleSoft}`, flexShrink: 0, background: PALETTE.card }}>
        <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.5, textTransform: 'uppercase', marginRight: 2 }}>View</span>
        {[['1', '1D'], ['3', '3D'], ['all', 'All']].map(([mode, label]) => (
          <button key={mode} onClick={() => { setColsMode(mode); setCustomInput(''); setDayOffset(0); }} style={{
            appearance: 'none', cursor: 'pointer', padding: '3px 8px', borderRadius: 5, fontFamily: 'JetBrains Mono', fontSize: 10, letterSpacing: 0.3,
            background: activeColsMode === (mode === 'all' ? 'all' : mode) ? PALETTE.ink : 'transparent',
            color: activeColsMode === (mode === 'all' ? 'all' : mode) ? PALETTE.paper : PALETTE.inkMute,
            border: `1px solid ${activeColsMode === (mode === 'all' ? 'all' : mode) ? PALETTE.ink : PALETTE.rule}`,
          }}>{label}</button>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, border: `1px solid ${activeColsMode === 'custom' ? PALETTE.ink : PALETTE.rule}`, borderRadius: 5, overflow: 'hidden', padding: '1px 5px', background: activeColsMode === 'custom' ? PALETTE.ink : 'transparent' }}>
          <input
            type="number" min={1} max={days.length}
            value={customInput}
            onChange={e => setCustomInput(e.target.value)}
            onBlur={applyCustomInput}
            onKeyDown={e => { if (e.key === 'Enter') applyCustomInput(); }}
            placeholder="N"
            style={{ width: 28, border: 0, background: 'transparent', fontFamily: 'JetBrains Mono', fontSize: 10, color: activeColsMode === 'custom' ? PALETTE.paper : PALETTE.inkMute, padding: 0, textAlign: 'center', appearance: 'textfield', MozAppearance: 'textfield', outline: 'none' }}
          />
          <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: activeColsMode === 'custom' ? PALETTE.paper : PALETTE.inkMute }}>D</span>
        </div>
      </div>

      {/* Day navigation header */}
      {days.length > colsPerView && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', borderBottom: `0.5px solid ${PALETTE.ruleSoft}`, flexShrink: 0, background: PALETTE.paper }}>
          <button
            onClick={() => setDayOffset(Math.max(0, safeOffset - 1))}
            disabled={safeOffset === 0}
            style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: 'transparent', borderRadius: 5, cursor: safeOffset === 0 ? 'default' : 'pointer', color: safeOffset === 0 ? PALETTE.rule : PALETTE.ink, padding: '3px 9px', fontSize: 14 }}
          >‹</button>
          <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 0.5, flex: 1, textAlign: 'center' }}>
            {visibleDays.map(d => `${d.label} ${d.date}`).join('  ·  ')}
          </span>
          <button
            onClick={() => setDayOffset(Math.min(maxOffset, safeOffset + 1))}
            disabled={safeOffset >= maxOffset}
            style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: 'transparent', borderRadius: 5, cursor: safeOffset >= maxOffset ? 'default' : 'pointer', color: safeOffset >= maxOffset ? PALETTE.rule : PALETTE.ink, padding: '3px 9px', fontSize: 14 }}
          >›</button>
        </div>
      )}

      {/* Day header row */}
      <div style={{ display: 'flex', flexShrink: 0, background: PALETTE.card, borderBottom: `0.5px solid ${PALETTE.ruleSoft}` }}>
        <div style={{ width: 44, flexShrink: 0 }} />
        {visibleDays.map((day, vi) => (
          <div key={vi} style={{ flex: 1, minWidth: MIN_COL_W, padding: '7px 8px', borderLeft: `0.5px solid ${PALETTE.ruleSoft}`, fontFamily: 'JetBrains Mono', fontSize: 10, letterSpacing: 0.5 }}>
            <span style={{ color: PALETTE.ink, fontWeight: 700 }}>Day {safeOffset + vi + 1}</span>
            {day.label && <span style={{ color: PALETTE.inkMute, marginLeft: 4, fontSize: 9 }}>{day.label} · {day.date}{day.destCity ? ` · ${day.destCity}` : ''}</span>}
          </div>
        ))}
      </div>

      {/* Scrollable grid */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', display: 'flex', alignItems: 'flex-start', scrollbarWidth: 'thin' }}>
        {/* Time gutter */}
        <div style={{ width: 44, flexShrink: 0, position: 'sticky', left: 0, zIndex: 3, background: PALETTE.paper, borderRight: `0.5px solid ${PALETTE.ruleSoft}`, minHeight: (CAL_END_HOUR - CAL_START_HOUR) * CAL_HOUR_PX }}>
          {CAL_HOURS.map(h => (
            <div key={h} style={{ height: CAL_HOUR_PX, display: 'flex', alignItems: 'flex-start', paddingTop: 4, paddingRight: 6, justifyContent: 'flex-end', fontFamily: 'JetBrains Mono', fontSize: 8, color: PALETTE.inkMute, letterSpacing: 0.3, lineHeight: 1, boxSizing: 'border-box' }}>
              {h === 12 ? '12pm' : h > 12 ? `${h - 12}pm` : `${h}am`}
            </div>
          ))}
        </div>

        {/* Day columns */}
        <div style={{ display: 'flex', flex: 1, minWidth: visibleDays.length * MIN_COL_W }}>
          {visibleDays.map((day, vi) => {
            const actualDayIdx = safeOffset + vi;
            const laneLayout = computeEventLanes(day.items, getStart, getDur);
            const dayBands = flightByDay[actualDayIdx] || [];
            return (
              <div key={vi} style={{ flex: 1, minWidth: MIN_COL_W, position: 'relative', borderLeft: `0.5px solid ${PALETTE.ruleSoft}`, boxSizing: 'border-box' }}>
                {/* Grid lines */}
                {CAL_HOURS.map(h => (
                  <React.Fragment key={h}>
                    <div style={{ position: 'absolute', top: (h - CAL_START_HOUR) * CAL_HOUR_PX, left: 0, right: 0, borderTop: `0.5px solid ${PALETTE.ruleSoft}`, pointerEvents: 'none' }} />
                    <div style={{ position: 'absolute', top: (h - CAL_START_HOUR + 0.5) * CAL_HOUR_PX, left: '20%', right: 0, borderTop: `0.5px solid ${PALETTE.ruleSoft}55`, pointerEvents: 'none' }} />
                  </React.Fragment>
                ))}
                <div style={{ height: (CAL_END_HOUR - CAL_START_HOUR) * CAL_HOUR_PX }} />

                {/* Events */}
                {day.items.map(slot => {
                  const start = getStart(slot);
                  const dur = getDur(slot);
                  const top = (Math.max(CAL_START_HOUR, start) - CAL_START_HOUR) * CAL_HOUR_PX;
                  const height = Math.max(dur * CAL_HOUR_PX, 28);
                  const cc = CAL_CAT_COLORS[slot.idea.cat] || CAL_CAT_COLORS.Custom;
                  const { col: laneCol = 0, totalCols: laneCols = 1 } = laneLayout.get(slot.idea.id) || {};
                  return (
                    <div
                      key={slot.idea.id}
                      style={{
                        position: 'absolute', top, height,
                        left: `calc(3px + (100% - 6px) / ${laneCols} * ${laneCol})`,
                        width: `calc((100% - 6px) / ${laneCols} - ${laneCols > 1 ? 2 : 0}px)`,
                        background: cc.bg, border: `1.5px solid ${cc.border}`,
                        borderRadius: 6, overflow: 'hidden', boxSizing: 'border-box', zIndex: 1,
                      }}
                    >
                      <div style={{ padding: '4px 6px' }}>
                        <div style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 600, color: cc.text, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {slot.idea.title}
                        </div>
                        {height > 44 && (
                          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: cc.text, opacity: 0.7, marginTop: 2 }}>
                            {fmtTime(start)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Flight events */}
                {dayBands.map((band, fi) => {
                    const dep = parseFlightHM(band.depTime);
                    const arr = parseFlightHM(band.arrTime);
                    if (dep === null && arr === null) return null;
                    const c = SHARED_FLIGHT_PALETTE[(band.seq ?? 0) % SHARED_FLIGHT_PALETTE.length];
                    const route = [band.origin, band.dest].filter(Boolean).join(' → ');
                    const startH = Math.max(CAL_START_HOUR, dep ?? arr);
                    const endH = Math.min(CAL_END_HOUR, arr ?? (dep + 1));
                    const durH = Math.max(endH - startH, 0.5);
                    const top = (startH - CAL_START_HOUR) * CAL_HOUR_PX;
                    const height = Math.max(durH * CAL_HOUR_PX, 30);
                    return (
                      <div key={fi} style={{
                        position: 'absolute', top, left: 3, right: 3, height,
                        background: c.bg, border: `1.5px solid ${c.border}`,
                        borderRadius: 6, zIndex: 5, pointerEvents: 'none',
                        boxSizing: 'border-box', padding: '3px 6px', overflow: 'hidden',
                      }}>
                        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: c.mono, fontWeight: 700, letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                          <span>✈️</span>
                          <span>{route || 'Flight'}</span>
                          {band.flightNo && <span style={{ fontWeight: 400, color: c.label }}>{band.flightNo}</span>}
                        </div>
                        {height >= 44 && (dep !== null || arr !== null) && (
                          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 8, color: c.label, marginTop: 1, whiteSpace: 'nowrap' }}>
                            {dep !== null ? fmtTime(dep) : '?'} → {arr !== null ? fmtTime(Math.min(arr, CAL_END_HOUR)) : '?'}
                            {band.origin && band.dest ? ` · ${band.origin}–${band.dest}` : ''}
                          </div>
                        )}
                      </div>
                    );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Accommodation footer row */}
      {hasAnyAccom && (
        <div style={{ display: 'flex', flexShrink: 0, borderTop: `0.5px solid ${PALETTE.ruleSoft}`, background: PALETTE.card }}>
          <div style={{ width: 44, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 6 }}>
            <span style={{ fontSize: 11 }}>🌙</span>
          </div>
          {visibleDays.map((_, vi) => {
            const dayIdx = safeOffset + vi;
            const accom = accommodations[dayIdx];
            const isBooked = accomBooked[dayIdx];
            return (
              <div key={vi} style={{ flex: 1, minWidth: MIN_COL_W, padding: '6px 8px', borderLeft: `0.5px solid ${PALETTE.ruleSoft}`, display: 'flex', alignItems: 'center', gap: 5, minHeight: 32 }}>
                {accom?.name ? (
                  <>
                    <span style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 500, color: PALETTE.ink, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{accom.name}</span>
                    {isBooked !== undefined && (
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: isBooked ? '#16A34A' : PALETTE.inkMute, letterSpacing: 0.3, flexShrink: 0 }}>
                        {isBooked ? '✓' : '·'}
                      </span>
                    )}
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main shared view ───────────────────────────────────────────────────────────
export function SharedItineraryView({ token }) {
  const [trip, setTrip] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [view, setView] = React.useState('list'); // 'list' | 'outlook'
  const [showMap, setShowMap] = React.useState(false);
  const [guests, setGuests] = React.useState([]);
  const [showNamePrompt, setShowNamePrompt] = React.useState(false);
  const [checkedIn, setCheckedIn] = React.useState(false);

  React.useEffect(() => {
    fetch(`/api/tp/share/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        setTrip(data.trip);
        setGuests(data.trip.guests || []);
      })
      .catch(e => setError(e.message || 'Failed to load itinerary'))
      .finally(() => setLoading(false));
  }, [token]);

  const checkIn = React.useCallback((name) => {
    const guestId = getOrCreateGuestId();
    return fetch(`/api/tp/share/${token}/guest`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guestId, name }),
    })
      .then(r => r.json())
      .then(data => { if (data.guests) setGuests(data.guests); })
      .catch(() => {});
  }, [token]);

  // Once the trip has loaded, either silently re-check-in (a name was
  // already given for this link on this device) or prompt for one — unless
  // they previously chose to skip it, in which case leave them alone.
  React.useEffect(() => {
    if (!trip || checkedIn) return;
    const savedName = localStorage.getItem(`tp_share_name_${token}`);
    const skipped = localStorage.getItem(`tp_share_skip_${token}`) === '1';
    if (savedName) { setCheckedIn(true); checkIn(savedName); }
    else if (!skipped) { setShowNamePrompt(true); }
  }, [trip, checkedIn, token, checkIn]);

  // Heartbeat so "viewing now" stays current for the owner and other guests.
  React.useEffect(() => {
    if (!checkedIn) return;
    const savedName = localStorage.getItem(`tp_share_name_${token}`);
    if (!savedName) return;
    const id = setInterval(() => checkIn(savedName), 30000);
    return () => clearInterval(id);
  }, [checkedIn, token, checkIn]);

  const submitGuestName = (name) => {
    localStorage.setItem(`tp_share_name_${token}`, name);
    setShowNamePrompt(false);
    setCheckedIn(true);
    checkIn(name);
  };
  const skipGuestName = () => {
    localStorage.setItem(`tp_share_skip_${token}`, '1');
    setShowNamePrompt(false);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: PALETTE.paper, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: 'Inter', fontSize: 14, color: PALETTE.inkMute }}>Loading itinerary…</div>
    </div>
  );

  if (error || !trip) return (
    <div style={{ minHeight: '100vh', background: PALETTE.paper, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontFamily: 'Fraunces', fontSize: 22, color: PALETTE.ink }}>Itinerary not found</div>
      <div style={{ fontFamily: 'Inter', fontSize: 14, color: PALETTE.inkMute }}>{error || 'This link may have expired.'}</div>
    </div>
  );

  const legs = trip.legs && trip.legs.length > 1 ? trip.legs : null;
  const destination = legs
    ? legs.map(l => (l.destination || '').split(',')[0].trim()).filter(Boolean).join(' → ')
    : (trip.destination || 'Trip');
  const city = destination.split(',')[0].trim();
  const ideaOverrides = trip.ideaOverrides || {};
  const destIdeas = legs ? getIdeasForLegs(legs) : getIdeasForDest(trip.destination);
  const customIdeas = (trip.customIdeas || []).map(idea => {
    const ov = ideaOverrides[idea.id] || {};
    return { ...idea, ...ov };
  });
  const allIdeas = [...customIdeas, ...destIdeas.map(idea => {
    const ov = ideaOverrides[idea.id] || {};
    return ov ? { ...idea, ...ov } : idea;
  })];
  const days = buildDisplayDays(trip, allIdeas);
  const accommodations = trip.accommodations || {};
  const accomBooked = trip.accomBooked || {};
  const dayNotes = trip.dayNotes || {};
  const flightByDay = trip.sharedFlights ? buildFlightByDay(trip.sharedFlights, days.length, trip.dateStart) : {};
  const dateRange = [trip.dateStart && fmtDate(trip.dateStart), trip.dateEnd && fmtDate(trip.dateEnd)].filter(Boolean).join(' – ');

  return (
    <div style={{ minHeight: '100vh', background: PALETTE.paper, display: 'flex', flexDirection: 'column' }}>
      {/* Hero */}
      <div style={{ padding: '24px 16px 16px', borderBottom: `1px solid ${PALETTE.rule}`, flexShrink: 0, maxWidth: 800, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 6 }}>Shared itinerary</div>
        <div style={{ fontFamily: 'Fraunces', fontSize: 28, letterSpacing: -0.8, color: PALETTE.ink, lineHeight: 1.1, marginBottom: 6 }}>{city}</div>
        {destination !== city && (
          <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft, marginBottom: 6 }}>{destination}</div>
        )}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {dateRange && <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkMute, letterSpacing: 0.5 }}>{dateRange}</div>}
          {trip.travelers > 1 && <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkMute, letterSpacing: 0.5 }}>{trip.travelers} travellers</div>}
          {trip.sharedHotel && <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.blue, letterSpacing: 0.5 }}>🏨 {trip.sharedHotel.name}</div>}
        </div>

        {guests.length > 0 && (
          <div
            style={{ marginTop: 8, fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, display: 'flex', alignItems: 'center', gap: 5 }}
            title={guests.slice(0, 4).map(g => `${g.name} — ${fmtLastSeen(g.lastSeen)}`).join(', ')}
          >
            <span>👥</span>
            <span>
              Viewing: {guests.slice(0, 4).map(g => g.name).join(', ')}
              {guests.length > 4 ? ` +${guests.length - 4} more` : ''}
            </span>
          </div>
        )}

        {/* View toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 2, background: PALETTE.paperDim, borderRadius: 8, padding: 3, border: `0.5px solid ${PALETTE.rule}` }}>
            {[{ id: 'list', label: 'List' }, { id: 'outlook', label: 'Outlook' }].map(({ id, label }) => (
              <button key={id} onClick={() => setView(id)} style={{
                appearance: 'none', border: 0, cursor: 'pointer', padding: '5px 14px', borderRadius: 6,
                background: view === id ? PALETTE.ink : 'transparent',
                color: view === id ? PALETTE.paper : PALETTE.inkMute,
                fontFamily: 'JetBrains Mono', fontSize: 10, letterSpacing: 0.5, fontWeight: view === id ? 600 : 400,
                transition: 'all 0.12s',
              }}>{label}</button>
            ))}
          </div>
          <button onClick={() => setShowMap(true)} style={{
            appearance: 'none', border: `0.5px solid ${PALETTE.rule}`, background: PALETTE.paperDim, cursor: 'pointer',
            padding: '5px 14px', borderRadius: 8, fontFamily: 'JetBrains Mono', fontSize: 10, letterSpacing: 0.5,
            color: PALETTE.inkMute, display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <span style={{ fontSize: 11 }}>📍</span> Map
          </button>
        </div>
      </div>

      {/* Content */}
      {view === 'outlook' ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <OutlookView
            days={days}
            ideaOverrides={ideaOverrides}
            accommodations={accommodations}
            accomBooked={accomBooked}
            flightByDay={flightByDay}
          />
        </div>
      ) : (
        <div style={{ maxWidth: 800, width: '100%', margin: '0 auto', padding: '0 16px 60px', boxSizing: 'border-box' }}>
          {days.map((day, di) => {
            const accom = accommodations[di];
            const note = dayNotes[di];
            const isAccomBooked = accomBooked[di];
            const dayBands = flightByDay[di] || [];
            return (
              <div key={di} style={{ borderLeft: `3px solid ${PALETTE.ruleSoft}`, paddingLeft: 16, marginTop: 28 }}>
                {/* Day header */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                  <div style={{ fontFamily: 'Fraunces', fontSize: 22, letterSpacing: -0.4, color: PALETTE.ink, lineHeight: 1 }}>Day {di + 1}</div>
                  <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                    {[day.label, day.date, day.destCity].filter(Boolean).join(' · ')}
                  </div>
                </div>

                {/* Flights for this day */}
                {dayBands.length > 0 && (
                  <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {dayBands.map(band => <FlightBand key={band.id} band={band} />)}
                  </div>
                )}

                {/* Day note */}
                {note && (
                  <div style={{ marginBottom: 10, padding: '6px 10px', borderRadius: 7, background: PALETTE.paperDim, fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {note}
                  </div>
                )}

                {/* Activities */}
                {day.items.length === 0 ? (
                  <div style={{ fontFamily: 'Fraunces', fontStyle: 'italic', fontSize: 14, color: PALETTE.inkMute, paddingBottom: 8 }}>
                    Open day — rest, wander, explore.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {day.items.map((slot, i) => {
                      const displayStart = ideaOverrides[slot.idea.id]?.startTime ?? slot.start;
                      const displayDur = ideaOverrides[slot.idea.id]?.dur ?? slot.idea.dur;
                      return (
                        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 0.3, width: 44, flexShrink: 0, paddingTop: 2, textAlign: 'right', lineHeight: 1.5 }}>
                            {fmtTime(displayStart)}<br />
                            <span style={{ opacity: 0.5, fontSize: 9 }}>{displayDur < 1 ? `${Math.round(displayDur * 60)}m` : `${displayDur}h`}</span>
                          </div>
                          <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: CAT_COLORS[slot.idea.cat] || PALETTE.inkMute, marginTop: 2, marginBottom: 2, flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
                            <div style={{ fontFamily: 'Fraunces', fontSize: 16, color: PALETTE.ink, lineHeight: 1.3 }}>{slot.idea.title}</div>
                            {(slot.idea.area || slot.idea.meta) && (
                              <div style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, marginTop: 2 }}>
                                {slot.idea.meta || slot.idea.area}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Tonight's accommodation */}
                {accom?.name && (
                  <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 7, padding: '5px 8px', borderRadius: 8, background: PALETTE.paperDim, border: `0.5px solid ${PALETTE.rule}` }}>
                    <span style={{ fontSize: 13 }}>🌙</span>
                    <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, fontWeight: 500, flex: 1, minWidth: 0 }}>{accom.name}</span>
                    {accom.notes && <span style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute }}>{accom.notes}</span>}
                    {isAccomBooked !== undefined && (
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 0.5, color: isAccomBooked ? '#16A34A' : PALETTE.inkMute, flexShrink: 0 }}>
                        {isAccomBooked ? '✓ booked' : '· unbooked'}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      {view === 'list' && (
        <div style={{ padding: '24px 16px', borderTop: `1px solid ${PALETTE.ruleSoft}`, textAlign: 'center', maxWidth: 800, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase' }}>Made with Travelplanner</div>
        </div>
      )}

      {showMap && (
        <MapView
          displayDays={days}
          displayOverflow={[]}
          allIdeas={allIdeas}
          destination={destination}
          timeOverrides={Object.fromEntries(
            Object.entries(ideaOverrides).map(([id, ov]) => [id, ov.startTime]).filter(([, v]) => v !== undefined)
          )}
          accent="#8B5CF6"
          onEdit={() => {}}
          onMoveToDay={() => {}}
          onMoveToParking={() => {}}
          onRemove={() => {}}
          onClose={() => setShowMap(false)}
        />
      )}

      {showNamePrompt && <GuestNamePrompt onSubmit={submitGuestName} onSkip={skipGuestName} />}
    </div>
  );
}
